import React, { useState } from "react";
import "./Login.css";
import LoginLogo from "../../Components/Assets/images/logo2.png";
import { Link, useNavigate } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import { account, tablesDB, DB_ID, USERS_TABLE } from "../../appwrite";
import { Eye, EyeOff } from "lucide-react";
import { useStateValue } from "../../StateProvider";

function Login() {
  useTitle("Login to TrendBay");
  const navigate = useNavigate();
  const [, dispatch] = useStateValue();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await account.createEmailPasswordSession(email, password);
      const user = await account.get();

      let userDoc = null;
      try {
        userDoc = await tablesDB.getRow({
          databaseId: DB_ID,
          tableId: USERS_TABLE,
          rowId: user.$id,
        });
      } catch {
        await account.deleteSession("current");
        setError("User account not found. Please contact support.");
        return;
      }

      if (userDoc.userRole !== "user" && userDoc.userRole !== "buyer") {
        await account.deleteSession("current");
        setError("ACCESS DENIED! Only buyers can log in.");
        return;
      }

      if (!userDoc.isVerified) {
        navigate("/account/verify-email");
        return;
      }

      // Update global state immediately so navbar reflects login
      dispatch({ type: "SET_USER", user: { ...user, ...userDoc } });

      // Send phone OTP if phone not yet verified
      if (user.phone && !user.phoneVerification) {
        try {
          await account.createPhoneVerification();
          navigate("/auth/verify/otp", {
            state: {
              userId: user.$id,
              phoneNumber: user.phone,
              flowType: "login",
            },
          });
          return;
        } catch {
          // OTP failed to send - still let user in
        }
      }

      navigate("/");
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("Invalid credentials") || msg.includes("password")) {
        setError("Email or password is incorrect.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <a href="/">
          <img className="auth-logo" src={LoginLogo} alt="TrendBay" />
        </a>
        <h1 className="auth-heading">Welcome back</h1>
        <p className="auth-sub">Sign in to your TrendBay account</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleLogin}>
          <div className="auth-field">
            <label className="auth-label">Email address</label>
            <input
              className="auth-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              required
            />
          </div>

          <div className="auth-field">
            <label className="auth-label">Password</label>
            <div className="auth-pw-wrap">
              <input
                className="auth-input"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                required
              />
              <span
                className="auth-pw-toggle"
                onClick={() => setShowPassword((p) => !p)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </span>
            </div>
          </div>

          <Link to="/account/reset-password" className="auth-forgot">
            Forgot password?
          </Link>

          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="auth-divider">or</div>

        <Link to="/signup" className="auth-btn-outline">
          Create a TrendBay Account
        </Link>

        <p className="auth-footer-text">
          By signing in you agree to our{" "}
          <a href="/privacy-policy">Privacy Policy</a> and{" "}
          <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
        </p>
      </div>
    </div>
  );
}

export default Login;
